<template>
	<div class="faq-groups">
		<InputPrimary
			v-model="searchQuery"
			class="faq-groups__search"
			prepend-inner="search-normal"
			:placeholder="$t('components.faq_page.search_placeholder')"
			:aria-label="$t('components.faq_page.search_placeholder')"
			clearable
			hide-details />

		<div v-if="groups.length > 1" class="faq-groups__tabs" role="tablist">
			<button
				v-for="tab in tabs"
				:key="tab.value"
				type="button"
				role="tab"
				class="faq-groups__tab"
				:class="{ 'faq-groups__tab--active': selectedTab === tab.value }"
				:aria-selected="selectedTab === tab.value"
				@click="selectedTab = tab.value">
				{{ tab.label }}
			</button>
		</div>

		<div v-if="filteredGroups.length" class="faq-groups__list">
			<section v-for="group in filteredGroups" :key="group.group" class="faq-groups__group">
				<h2 class="faq-groups__group-title">{{ group.group }}</h2>
				<FaqAccordion :questions="group.questions" />
			</section>
		</div>
		<p v-else class="faq-groups__no-results">{{ $t('components.faq_page.no_results') }}</p>
	</div>
</template>

<script setup lang="ts">
const ALL_TAB = 'all'

const props = defineProps<{
	groups: IFaqGroup[]
}>()

const searchQuery = ref<string | null>('')
const selectedTab = ref<number | typeof ALL_TAB>(ALL_TAB)

const { t } = useI18n()
const tabs = computed(() => [
	{ value: ALL_TAB, label: t('components.faq_page.all') },
	...props.groups.map((group, idx) => ({ value: idx, label: group.group })),
])

// case- and diacritic-insensitive, so "zwrot" also matches "Zwrót"
const normalize = (text: string) =>
	text
		.toLocaleLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036F]/g, '')
		.replace(/ł/g, 'l')

const filteredGroups = computed(() => {
	const query = normalize(searchQuery.value?.trim() ?? '')
	const groupsInTab = selectedTab.value === ALL_TAB ? props.groups : [props.groups[selectedTab.value]].filter(Boolean)

	if (!query) return groupsInTab

	return groupsInTab
		.map((group) => ({
			...group,
			questions: group.questions.filter((item) => normalize(`${item.question} ${item.answer}`).includes(query)),
		}))
		.filter((group) => group.questions.length)
})

// structured data always describes every question on the page, regardless of the active filter
useFaqJsonld(() => props.groups.flatMap((group) => group.questions))
</script>

<style lang="scss" scoped src="./FaqGroups.scss"></style>
